import {
  EventEmitter,
  Component,
  Input,
  Output,
  ViewChild,
  inject,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import createDebug from 'debug';
import { AuthService } from '../../services/auth/auth.service';
import type { AvatarMenuItem } from '../../interface-definitions/index';
import { AsyncPipe } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { AdElDirective } from '../../directives/ad-el/ad-el.directive';

const Debug = createDebug('AvatarComponent');

/**
 * Avatar
 *
 * User avatar button with a menu showing the current user email and logout.
 * Shows a "Switch User" option when impersonation is enabled. Additional
 * custom links can be supplied via the `menuItems` input.
 */
@Component({
  selector: 'avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  host: { adComp: '' },
  imports: [
    AdElDirective,
    AsyncPipe,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class AvatarComponent implements OnInit {
  authService = inject(AuthService);
  /** Menu Items - Custom menu links shown above Switch User / Logout */ @Input()
  menuItems?: AvatarMenuItem[];

  /** On Menu Item Click - Emitted when a menu item without a path is clicked */ @Output()
  onMenuItemClick: EventEmitter<any> = new EventEmitter<any>();

  /** Allow Impersonation - Whether the server allows user switching */
  allowImpersonation = false;

  /** Initialize - Check if impersonation is available */

  async initialize(): Promise<void> {
    const debug = Debug.extend('initialize');
    debug('initialize, menuItems=%o', this.menuItems);
    const config = await this.authService.getAuthConfig();
    this.allowImpersonation = !!config?.allowImpersonation;
    debug('allowImpersonation=%s', this.allowImpersonation);
  }

  /** Handle Menu Item Click - Navigate if path is set, otherwise emit the item */
  handleMenuItemClick(item: AvatarMenuItem): void {
    const debug = Debug.extend('handleMenuItemClick');
    debug('handleMenuItemClick %o', item);
    if (item.path) {
      this.router.navigateByUrl(item.path);
    } else {
      this.onMenuItemClick.emit(item);
    }
  }

  /** Switch User - Navigate to the switch user page */
  switchUser(): void {
    const debug = Debug.extend('switchUser');
    debug('switchUser');
    this.router.navigate(['/switch-user']);
  }

  /** Logout */
  logout(): void {
    const debug = Debug.extend('logout');
    debug('logout');
    this.authService.logout();
  }

  /** User Menu - Reference to the menu component */
  @ViewChild('userMenu')
  userMenu!: any;
  private router = inject(Router);

  /** Access Tokens - Navigate to the personal access tokens page */
  accessTokens(): void {
    const debug = Debug.extend('accessTokens');
    debug('accessTokens');
    this.router.navigate(['/personal-access-tokens']);
  }

  ngOnInit(): void {
    this.initialize();
  }
}
